import {
  DEFAULT_STANDING_LATENCY_THRESHOLDS,
  LATENCY_BUDGET_GLASS_TO_GLASS_MS,
  classifyLinkImpairment,
  RECLAIM_WITHIN_BUDGET_MS,
  TARGET_FRAMERATE,
  canReclaim,
  isDebugWebrtcLoggingEnabled,
} from '@playcastdotio/PeerCore';
import type {
  CalibratedLatencySummary,
  ReclaimEvidence,
  SessionDowngradeFloor,
  StandingLatencyVerdict,
  StickyDowngradePolicy,
} from '@playcastdotio/PeerCore';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';

/** A release followed by a for-cause raise inside this window is an oscillation. */
export const RECLAIM_RECUT_WINDOW_MS = 30_000;
/** Each oscillation doubles the evidence window for the peer, up to this multiple. */
export const RECLAIM_WINDOW_MAX_MULTIPLIER = 8;

/** The fps rungs a release restores through, lowest first (the decode-lag ladder, inverted). */
const FPS_RECLAIM_LADDER: ReadonlyArray<number> = [30, 45, TARGET_FRAMERATE];

/** One rung down the sticky-floor ladder. Every release path must use this, not a jump to 'none'. */
export function rungBelow(floor: SessionDowngradeFloor): SessionDowngradeFloor {
  if (floor === 'tier360') return 'hard';
  if (floor === 'hard') return 'soft';
  return 'none';
}

/** The next fps rung above `ceiling`, or undefined when already at the top. */
export function nextFpsRungAbove(ceiling: number): number | undefined {
  return FPS_RECLAIM_LADDER.find((rung) => rung > ceiling);
}

export type StickyFloorReclaimDeps = {
  stickyDowngrade: StickyDowngradePolicy;
  getCalibratedLatency?: (peerId: string) => Pick<CalibratedLatencySummary, 'lastMs' | 'floorMs'> | undefined;
  getStandingLatency?: (peerId: string) => StandingLatencyVerdict | undefined;
  /**
   * Phase 7 hook: a standing-buffer drain in progress is not reclaim evidence. Wired as a
   * pull so the drain manager needs no reference to this one; absent means "never draining".
   */
  isDraining?: (peerId: string) => boolean;
  /** A `tier360 → hard` release lands the resolution change on a keyframe. */
  requestKeyframeOnce: (peerId: string) => void;
  onReleased: (peerId: string, from: SessionDowngradeFloor, to: SessionDowngradeFloor, reason: string) => void;
  /** Glass-to-glass budget the evidence is measured against. Default LATENCY_BUDGET_GLASS_TO_GLASS_MS. */
  budgetMs?: number;
  /** Continuous within-budget time per rung. Default RECLAIM_WITHIN_BUDGET_MS. */
  reclaimWindowMs?: number;
  nowMs?: () => number;
};

type PeerReclaimEvidence = {
  withinSinceMs: number | null;
  lastObservedAtMs: number | null;
  lossPctMax: number;
  standingQueueMsMax: number;
  lastReleaseAtMs: number | null;
  windowMultiplier: number;
};

/**
 * StickyFloorReclaimManager — PRODUCT DECISION D1 as amended: a floor raised for cause is
 * released, one rung at a time, on MEASURED evidence that the cause is gone.
 *
 * Evidence is continuous within-budget glass-to-glass time from the calibrated tracker with
 * no loss, no queue and no standing buffer for the whole window. Any adverse tick — over
 * budget, loss above `maxLossPct`, queue above `maxQueueMs`, a `standing-buffer` verdict, an
 * unmeasured reading, or a drain in progress — resets the clock. When `canReclaim` holds the
 * floor drops one rung (`tier360 → hard → soft → none`), the fps ceiling rises one rung, and
 * the owner re-primes the encoding through `onReleased`.
 *
 * Android-safe climb-back: leaving `tier360` (the resolution change) additionally waits for
 * the depth hypothesis to be anything but `frame-constant`, and requests a keyframe so the
 * decoder reconfiguration lands on an IDR. A release followed by a fresh cut within
 * RECLAIM_RECUT_WINDOW_MS doubles the peer's evidence window (logged) so cut/reclaim/cut
 * cannot oscillate.
 */
export class StickyFloorReclaimManager {
  private readonly peers = new Map<string, PeerReclaimEvidence>();
  private readonly budgetMs: number;
  private readonly reclaimWindowMs: number;
  private readonly nowMs: () => number;

  constructor(private readonly deps: StickyFloorReclaimDeps) {
    this.budgetMs = deps.budgetMs ?? LATENCY_BUDGET_GLASS_TO_GLASS_MS;
    this.reclaimWindowMs = deps.reclaimWindowMs ?? RECLAIM_WITHIN_BUDGET_MS;
    this.nowMs = deps.nowMs ?? (() => Date.now());
  }

  /** Call once per host stats tick with the tick's loss rate and standing queue. */
  public tick(peerId: string, sample: { lossPct?: number; standingQueueMs?: number; rttMs?: number }): void {
    const floor = this.deps.stickyDowngrade.floorFor(peerId);
    const evidence = this.evidenceFor(peerId);
    if (floor === 'none') {
      this.resetClock(evidence);
      return;
    }
    const now = this.nowMs();
    if (!Number.isFinite(now) || (evidence.lastObservedAtMs !== null
      && (now <= evidence.lastObservedAtMs || now - evidence.lastObservedAtMs >= 3_000))) this.resetClock(evidence);
    if (!Number.isFinite(now)) return;
    evidence.lastObservedAtMs = now;
    const verdict = this.deps.getStandingLatency?.(peerId);
    const latencyMs = this.deps.getCalibratedLatency?.(peerId)?.lastMs ?? null;
    if (this.isAdverse(peerId, latencyMs, sample, verdict)) {
      this.resetClock(evidence);
      return;
    }
    if (evidence.withinSinceMs === null) evidence.withinSinceMs = now;
    // A flat-queue loss burst is not the congestion that raised the floor. Counting it
    // would keep lossPctMax above the reclaim threshold for the whole session.
    if (classifyLinkImpairment(this.impairmentSample(sample)) !== 'wireless-loss') {
      evidence.lossPctMax = Math.max(evidence.lossPctMax, sample.lossPct ?? 0);
    }
    evidence.standingQueueMsMax = Math.max(evidence.standingQueueMsMax, sample.standingQueueMs ?? 0);

    const reclaimEvidence: ReclaimEvidence = {
      withinBudgetForMs: now - evidence.withinSinceMs,
      standingLatencyKind: verdict?.kind ?? 'unknown',
      lossPctMax: evidence.lossPctMax,
      standingQueueMsMax: evidence.standingQueueMsMax,
    };
    const windowMs = this.reclaimWindowMs * evidence.windowMultiplier;
    if (!canReclaim(reclaimEvidence, { withinBudgetMs: windowMs })) return;
    if (floor === 'tier360' && verdict?.depthHypothesis === 'frame-constant') {
      // The decoder is holding a fixed number of frames in flight: a resolution switch
      // reconfigures it and would re-inflate the very buffer this evidence says is gone.
      this.debug('[Reclaim.hold]', { peerId, floor, reason: 'frame-constant depth hypothesis' });
      return;
    }
    this.release(peerId, floor, evidence, now);
  }

  /**
   * A for-cause floor raise. Inside RECLAIM_RECUT_WINDOW_MS of a release it is an oscillation:
   * the next window for this peer doubles (bounded), and the clock restarts either way.
   */
  public noteFloorRaised(peerId: string): void {
    const evidence = this.evidenceFor(peerId);
    const now = this.nowMs();
    if (evidence.lastReleaseAtMs !== null && now - evidence.lastReleaseAtMs <= RECLAIM_RECUT_WINDOW_MS) {
      evidence.windowMultiplier = Math.min(evidence.windowMultiplier * 2, RECLAIM_WINDOW_MAX_MULTIPLIER);
      this.debug('[Reclaim.oscillation]', {
        peerId,
        sinceReleaseMs: now - evidence.lastReleaseAtMs,
        nextWindowMs: this.reclaimWindowMs * evidence.windowMultiplier,
      });
    }
    this.resetClock(evidence);
  }

  /** Continuous within-budget evidence accumulated so far, ms — for tests and debug lines. */
  public withinBudgetForMs(peerId: string): number {
    const evidence = this.peers.get(peerId);
    const now = this.nowMs();
    if (!evidence || evidence.withinSinceMs === null || evidence.lastObservedAtMs === null
      || now < evidence.lastObservedAtMs || now - evidence.lastObservedAtMs >= 3_000) return 0;
    return now - evidence.withinSinceMs;
  }

  public invalidateEvidence(peerId: string): void {
    const evidence = this.peers.get(peerId);
    if (evidence) this.resetClock(evidence);
  }

  public clearPeer(peerId: string): void {
    this.peers.delete(peerId);
  }

  public clearAll(): void {
    this.peers.clear();
  }

  private impairmentSample(sample: { lossPct?: number; standingQueueMs?: number; rttMs?: number }) {
    const queue = sample.standingQueueMs;
    return {
      packetsLostPct: sample.lossPct ?? 0,
      standingQueueMs: queue ?? null,
      ...(typeof queue === 'number' ? {} : { rttMs: sample.rttMs }),
    };
  }

  private isAdverse(
    peerId: string,
    latencyMs: number | null,
    sample: { lossPct?: number; standingQueueMs?: number; rttMs?: number },
    verdict: StandingLatencyVerdict | undefined,
  ): boolean {
    if (latencyMs === null || !Number.isFinite(latencyMs)) return true;
    const wirelessLoss = classifyLinkImpairment(this.impairmentSample(sample)) === 'wireless-loss';
    // The latency jump during a flat-queue loss burst is the starved picture, not a
    // standing buffer. Counting it would make the floor that caused the stall unreleasable.
    if (!wirelessLoss && latencyMs > this.budgetMs) return true;
    if (!wirelessLoss && (sample.lossPct ?? 0) > DEFAULT_STANDING_LATENCY_THRESHOLDS.maxLossPct) return true;
    if ((sample.standingQueueMs ?? 0) > DEFAULT_STANDING_LATENCY_THRESHOLDS.maxQueueMs) return true;
    if (verdict?.kind === 'standing-buffer') return true;
    return this.deps.isDraining?.(peerId) === true;
  }

  private release(peerId: string, from: SessionDowngradeFloor, evidence: PeerReclaimEvidence, now: number): void {
    const to = rungBelow(from);
    const reason = 'reclaim:within-budget';
    this.deps.stickyDowngrade.release(peerId, to, reason);
    const ceiling = this.deps.stickyDowngrade.fpsCeilingFor(peerId, TARGET_FRAMERATE);
    const nextFps = FPS_RECLAIM_LADDER.find((rung) => rung > ceiling);
    if (nextFps !== undefined) this.deps.stickyDowngrade.raiseFpsCeiling(peerId, nextFps);
    if (from === 'tier360') this.deps.requestKeyframeOnce(peerId);
    evidence.lastReleaseAtMs = now;
    this.resetClock(evidence);
    this.debug('[Reclaim.release]', { peerId, from, to, fpsCeiling: nextFps ?? ceiling, reason });
    this.deps.onReleased(peerId, from, to, reason);
  }

  private resetClock(evidence: PeerReclaimEvidence): void {
    evidence.withinSinceMs = null;
    evidence.lastObservedAtMs = null;
    evidence.lossPctMax = 0;
    evidence.standingQueueMsMax = 0;
  }

  private evidenceFor(peerId: string): PeerReclaimEvidence {
    let evidence = this.peers.get(peerId);
    if (!evidence) {
      evidence = { withinSinceMs: null, lastObservedAtMs: null, lossPctMax: 0, standingQueueMsMax: 0, lastReleaseAtMs: null, windowMultiplier: 1 };
      this.peers.set(peerId, evidence);
    }
    return evidence;
  }

  private debug(tag: string, line: Record<string, unknown>): void {
    if (!isDebugWebrtcLoggingEnabled()) return;
    console.log(tag, line);
    sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag, ...line }));
  }
}
